import {
  Assignee,
  AssigneePicker,
  Badge,
  BadgeVariant,
  Button,
  Flex,
  Icon,
  PickerStatus,
  Typography
} from '@percent/lemonade'
import { TasksListProps } from './TasksList.types'
import { TFunction, useTranslation } from 'react-i18next'
import { NonprofitTask, TaskType } from '@percent/cause-dashboard/services/grants/grantsService.types'
import { LocaleKey } from '@percent/cause-dashboard/i18n/config'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import styles from './TasksList.module.scss'
import { RoutePath } from '@percent/cause-dashboard/app/routes/Routes'
import { useLocale } from '@percent/cause-dashboard/common/hooks/useLocale/useLocale'
import { formatDate } from '@percent/cause-dashboard/common/utility/date'
import { useTaskAssignees } from './hooks/useTaskAssignees'

function renderTaskAction({
  task,
  status,
  assignee,
  navigate,
  selectTask,
  currentUser,
  t
}: {
  task: NonprofitTask
  status: Exclude<PickerStatus, 'cancelled'>
  assignee: Assignee | undefined
  navigate: NavigateFunction
  selectTask: (taskId: string) => void
  currentUser: { id: string }
  t: TFunction
}) {
  const bankDetailsProfileUrl = `${RoutePath.PROFILE}?section=bank-details`
  const isBankDetails = task.type === TaskType.BANK_DETAILS
  const isAssignee = assignee?.id === currentUser.id

  if (status === 'completed' && isBankDetails) {
    return (
      <Button
        variant="secondary"
        size="small"
        data-testid="view-bank-details-button"
        onClick={() => navigate(bankDetailsProfileUrl)}
      >
        {t('grants.grantDetails.candidateTasks.seeBankDetails')}
      </Button>
    )
  }

  if (status === 'completed' && task.type === TaskType.REVIEW) {
    return (
      <Button variant="secondary" size="small" data-testid="view-submission-button" onClick={() => selectTask(task.id)}>
        {t('grants.grantDetails.candidateTasks.viewSubmission')}
      </Button>
    )
  }

  if (status !== 'completed') {
    return (
      <Button
        variant="secondary"
        size="small"
        data-testid="submit-task-button"
        onClick={() => (isBankDetails ? navigate(bankDetailsProfileUrl) : selectTask(task.id))}
      >
        {isAssignee
          ? t('grants.grantDetails.candidateTasks.completeTask')
          : t('grants.grantDetails.candidateTasks.view')}
      </Button>
    )
  }

  return null
}

const getTaskStatus = (
  task: NonprofitTask
): { status: Exclude<PickerStatus, 'cancelled' | 'wont_do'>; variant: BadgeVariant; label: LocaleKey } => {
  const isCompleted = task.status === 'done'
  if (isCompleted)
    return { status: 'completed', variant: 'positive', label: 'grants.grantDetails.candidateTasks.complete' }

  const now = Date.now()
  const dueTime = task.dueByDate ? new Date(task.dueByDate).getTime() : 0
  const isOverdue = !isNaN(dueTime) && dueTime < now

  if (isOverdue) return { status: 'overdue', variant: 'critical', label: 'grants.grantDetails.candidateTasks.overdue' }

  return { status: 'todo', variant: 'default', label: 'grants.grantDetails.candidateTasks.todo' }
}

export const sortTasksByPriority = (tasks: NonprofitTask[]): NonprofitTask[] => {
  return [...tasks].sort((a, b) => {
    const aStatus = getTaskStatus(a).status
    const bStatus = getTaskStatus(b).status

    const statusPriority = { overdue: 0, todo: 1, completed: 2 }

    if (statusPriority[aStatus] !== statusPriority[bStatus]) {
      return statusPriority[aStatus] - statusPriority[bStatus]
    }

    if (a.order !== null && b.order !== null) {
      if (a.order !== b.order) return a.order - b.order
    } else if (a.order !== null) {
      return -1
    } else if (b.order !== null) {
      return 1
    } else {
      const aTime = a.dueByDate ? new Date(a.dueByDate).getTime() : 0
      const bTime = b.dueByDate ? new Date(b.dueByDate).getTime() : 0
      if (aTime !== bTime) return aTime - bTime
    }

    return a.id.localeCompare(b.id)
  })
}

export const TasksList = ({ tasks, selectTask, refetchTasks }: Readonly<TasksListProps>) => {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const navigate = useNavigate()

  const { currentUser, organisationOwners, search, updateTaskAssignee, getAssignee } = useTaskAssignees()

  const onAssigneeChange = async (taskId: string, assignee: Assignee) => {
    await updateTaskAssignee(taskId, assignee)
    refetchTasks()
  }

  return (
    <Flex direction="column" gap={16} mt={16} data-testid="tasks-list">
      {sortTasksByPriority(tasks).map(task => {
        const { status, variant, label } = getTaskStatus(task)
        const assignee = getAssignee(task.id, task.assignment.assignedTo)

        return (
          <Flex key={task.id} data-testid={`task-${task.id}`} className={styles.taskContainer} data-status={status}>
            <Flex direction="row" justify="space-between">
              <Badge variant={variant} color="gray">
                {task.name}
              </Badge>

              {task.dueByDate && (
                <Flex align="center" className={styles.deadline}>
                  <Icon name="clock" size={'s'} />
                  <Typography variant="small" data-testid="task-due-date">
                    {formatDate(task.dueByDate, locale)}
                  </Typography>
                </Flex>
              )}
            </Flex>

            <Flex className={styles.taskWrapper}>
              <AssigneePicker
                assignee={assignee}
                status={status}
                statusLabel={t(label)}
                assigneeList={organisationOwners}
                tooltipContent={t('grants.grantDetails.candidateTasks.tooltip', { assignee: assignee?.email })}
                search={{ ...search, placeholder: t('grants.grantDetails.candidateTasks.searchByEmail') }}
                noResultsMessage={t('grants.grantDetails.candidateTasks.noAssigneeResults')}
                onAssigneeChange={assignee => onAssigneeChange(task.id, assignee)}
              />

              {renderTaskAction({ task, status, assignee, navigate, selectTask, currentUser, t })}
            </Flex>
          </Flex>
        )
      })}
    </Flex>
  )
}
